// MedicalCertEditor.vue
<template>
  <b-modal
    :id="modalId"
    :ref="modalRef"
    size="xl"
    no-close-on-backdrop
    :modal-class="['medical-cert-editor-modal']"
    content-class="modal-content-custom"
    hide-footer
  >
    <template #modal-title>
      <div class="mce-title-bar">
        <span class="mce-title-icon">
          <i class="fas fa-file-medical"></i>
        </span>
        <span class="mce-title-text">
          {{ isTh ? "ใบรับรองแพทย์" : "Medical Certificate" }}
        </span>
        <span v-if="patient && patient.id" class="mce-title-sub">
          {{ ptFullnameDisplay }}
          <template v-if="patient.dn">· DN {{ patient.dn }}</template>
        </span>
        <b-button-group size="sm" class="mce-lang">
          <b-button
            :variant="lang === 'th' ? 'dark' : 'outline-secondary'"
            @click="lang = 'th'"
          >TH</b-button>
          <b-button
            :variant="lang === 'en' ? 'dark' : 'outline-secondary'"
            @click="lang = 'en'"
          >EN</b-button>
        </b-button-group>
      </div>
    </template>

    <div class="mce-body">
      <!-- Left: treatment picker -->
      <aside class="mce-picker">
        <div class="mce-picker-head">
          <div class="mce-picker-title">
            <span>{{ isTh ? "รายการรักษา" : "Treatments" }}</span>
            <span class="mce-picker-count">
              {{ selectedItem.length }} / {{ treatmentItems.length }}
            </span>
          </div>
          <b-form-input
            v-model="pickerSearch"
            size="sm"
            :placeholder="isTh ? 'ค้นหา ซี่ฟัน · วินิจฉัย · แพทย์' : 'Search tooth · diagnosis · doctor'"
            class="mce-picker-search"
          />
        </div>
        <div class="mce-picker-list">
          <div
            v-if="!filteredTreatmentItems.length"
            class="mce-picker-empty"
          >
            {{ isTh ? "ไม่มีรายการรักษา" : "No treatments" }}
          </div>
          <button
            v-for="item in filteredTreatmentItems"
            :key="item.id"
            type="button"
            class="mce-row"
            :class="{ 'mce-row--selected': isSelected(item) }"
            @click="toggleSelected(item)"
          >
            <span class="mce-row-check">
              <i v-if="isSelected(item)" class="fas fa-check-square"></i>
              <i v-else class="far fa-square"></i>
            </span>
            <span class="mce-row-body">
              <span class="mce-row-line1">
                <span class="mce-row-title">
                  {{ item.name || (isTh ? "(ไม่ระบุรายการ)" : "(unnamed)") }}
                </span>
                <span class="mce-row-date">{{ formatDate(item.creationDt) }}</span>
              </span>
              <span v-if="item.dxName" class="mce-row-dx">
                {{ item.dxName }}
              </span>
              <span class="mce-row-meta">
                <span
                  v-if="(item.toothNumberSurfaces || '').trim()"
                  class="mce-row-tooth"
                >{{ item.toothNumberSurfaces.trim() }}</span>
                <span
                  v-if="(item.toothNumberSurfaces || '').trim() && item.doctorName"
                  class="mce-row-sep"
                >·</span>
                <span v-if="item.doctorName">{{ item.doctorName }}</span>
              </span>
            </span>
          </button>
        </div>
      </aside>

      <!-- Right: doc canvas -->
      <section class="mce-canvas">
        <div class="mce-paper" ref="paper">
          <div ref="body">
            <MedicalCertTemplate :document-data="documentData">
              <template #header-toolbar>
                <div class="mce-headzone">
                  <div class="mce-headzone-grp">
                    <span class="mce-headzone-lbl">
                      {{ isTh ? "โลโก้" : "Logo" }}
                    </span>
                    <b-button-group size="sm">
                      <b-button
                        :variant="showLogo ? 'dark' : 'outline-secondary'"
                        @click="showLogo = true"
                      >{{ isTh ? "แสดง" : "Show" }}</b-button>
                      <b-button
                        :variant="!showLogo ? 'dark' : 'outline-secondary'"
                        @click="showLogo = false"
                      >{{ isTh ? "ซ่อน" : "Hide" }}</b-button>
                    </b-button-group>
                  </div>
                  <div class="mce-headzone-grp">
                    <span class="mce-headzone-lbl">
                      {{ isTh ? "หัวกระดาษ" : "Header" }}
                    </span>
                    <b-button-group size="sm">
                      <b-button
                        :variant="headerFormat === 'clinic_branch' ? 'dark' : 'outline-secondary'"
                        @click="headerFormat = 'clinic_branch'"
                      >{{ isTh ? "คลินิก + สาขา" : "Clinic + Branch" }}</b-button>
                      <b-button
                        :variant="headerFormat === 'clinic' ? 'dark' : 'outline-secondary'"
                        @click="headerFormat = 'clinic'"
                      >{{ isTh ? "คลินิก" : "Clinic" }}</b-button>
                      <b-button
                        :variant="headerFormat === 'branch' ? 'dark' : 'outline-secondary'"
                        @click="headerFormat = 'branch'"
                      >{{ isTh ? "สาขา" : "Branch" }}</b-button>
                    </b-button-group>
                  </div>
                </div>
              </template>

              <template #doctor-token>
                <b-dropdown
                  variant="link"
                  no-caret
                  toggle-class="mce-pill"
                  menu-class="mce-pill-menu"
                  :class="{ 'mce-pill--invalid': $v.title.$error }"
                  :title="$v.title.$error ? (isTh ? 'กรุณาเลือกคำนำหน้า' : 'Please select a title') : ''"
                >
                  <template #button-content>
                    <span v-if="title">{{ titleDisplay }}</span>
                    <span v-else class="mce-pill-placeholder">
                      {{ isTh ? "คำนำหน้า" : "Title" }}
                    </span>
                  </template>
                  <b-dropdown-item
                    v-for="opt in titleOptions"
                    :key="opt"
                    @click="title = opt; $v.title.$touch()"
                  >{{ opt }}</b-dropdown-item>
                </b-dropdown><b-dropdown
                  variant="link"
                  no-caret
                  toggle-class="mce-pill"
                  menu-class="mce-pill-menu mce-pill-menu--wide"
                  :class="{ 'mce-pill--invalid': $v.selectId.$error }"
                  :title="$v.selectId.$error ? (isTh ? 'กรุณาเลือกแพทย์' : 'Please select a doctor') : ''"
                >
                  <template #button-content>
                    <span v-if="doctorDisplayName">
                      {{ doctorDisplayName }}
                    </span>
                    <span v-else class="mce-pill-placeholder">
                      {{ isTh ? "เลือกแพทย์" : "Select doctor" }}
                    </span>
                  </template>
                  <b-dropdown-form class="mce-doctor-search">
                    <b-form-input
                      v-model="doctorSearch"
                      size="sm"
                      :placeholder="isTh ? 'ค้นหาแพทย์' : 'Search doctor'"
                      @click.stop
                    />
                  </b-dropdown-form>
                  <b-dropdown-divider />
                  <b-dropdown-item
                    v-for="d in filteredDoctorList"
                    :key="d.uid"
                    :active="d.uid === selectId"
                    @click="selectId = d.uid; $v.selectId.$touch()"
                  >
                    <div class="mce-doctor-row">
                      <span class="mce-doctor-name">{{ d.fullName }}</span>
                      <span v-if="hasLicense(d)" class="mce-doctor-lic">
                        ท. {{ d.drLicenseNo }}
                      </span>
                    </div>
                  </b-dropdown-item>
                  <b-dropdown-text v-if="!filteredDoctorList.length" class="text-muted">
                    {{ isTh ? "ไม่พบแพทย์" : "No doctors" }}
                  </b-dropdown-text>
                </b-dropdown>
              </template>

              <template #comment>
                <div
                  v-if="commentDetail || isEditingComment"
                  ref="commentEditor"
                  class="mce-comment-edit"
                  contenteditable
                  spellcheck="false"
                  @input="onCommentInput"
                  @blur="onCommentBlur"
                ></div>
                <div
                  v-else
                  class="mc-rules mce-comment-empty"
                  :title="isTh ? 'คลิกเพื่อพิมพ์ความเห็น' : 'Click to type opinion'"
                  @click="startEditingComment"
                >
                  <div v-for="n in 4" :key="n" class="mc-rule"></div>
                </div>
              </template>
            </MedicalCertTemplate>
          </div>
        </div>
      </section>
    </div>

    <div class="mce-footer">
      <b-button
        variant="outline-danger"
        size="sm"
        :disabled="isPdfCreating"
        @click="clearAll"
      >
        <i class="fas fa-eraser mr-1"></i>
        {{ isTh ? "เคลียร์" : "Clear" }}
      </b-button>
      <span class="mce-status">{{ statusMessage }}</span>
      <b-button
        variant="outline-info"
        size="sm"
        class="mr-2"
        :disabled="isPdfCreating"
        @click="uploadPdf"
      >
        <i class="fas fa-signature mr-1"></i>
        {{ isTh ? "อัปโหลด" : "Upload" }}
      </b-button>
      <b-button
        variant="primary"
        size="sm"
        :disabled="isPdfCreating"
        @click="generatePdf"
      >
        <i class="fas fa-print mr-1"></i>
        {{ isTh ? "พิมพ์" : "Print" }}
      </b-button>
    </div>

    <PdfExport
      ref="pdfExport"
      @openSignTabCompleted="hideModal"
      @showSignDialog="handleShowSignDialog"
    />
    <Dialog ref="Dialog" />
  </b-modal>
</template>

<script>
import { mapGetters } from "vuex";
import moment from "moment";
import { required } from "vuelidate/lib/validators";
import Dialog from "@/components/modal/Dialog";
import PdfExport from "@/components/PdfExport";
import MedicalCertTemplate from "./templates/MedicalCertTemplate.vue";

export default {
  name: "MedicalCertEditor",
  components: {
    PdfExport,
    Dialog,
    MedicalCertTemplate,
  },
  props: {
    modalId: {
      type: String,
      default: "medicalCert-modal",
    },
    modalRef: {
      type: String,
      default: "medicalCertModal",
    },
  },
  data() {
    return {
      patient: {},
      clinic: {},
      logo: null,
      titleOptions: ["ทพ.", "ทพญ."],
      title: null,
      selectId: null,
      commentDetail: "",
      selectedItem: [],
      isPdfCreating: false,
      lang: "th",
      showLogo: true,
      headerFormat: "clinic_branch",
      pickerSearch: "",
      doctorSearch: "",
      isEditingComment: false,
    };
  },
  validations: {
    title: { required },
    selectId: { required },
  },
  computed: {
    ...mapGetters({
      branchInfo: "moduleUser/getBranchInfo",
      getTreatmentHistoryList: "moduleTreatment/getTreatmentHistoryList",
      getTreatDiagBy: "moduleAppointment/getTreatDiagBy",
      getDoctorList: "moduleUser/getDoctorList",
      getUserList: "moduleUser/getUserList",
      getUserListIncludeDeletedBy: "moduleUser/getUserListIncludeDeletedBy",
      userInfo: "moduleUser/getUserInfo",
      isDoctor: "moduleUser/isDoctor",
      viewFile: "moduleContent/viewFile",
    }),
    isTh() {
      return this.lang !== "en";
    },
    today() {
      return moment().locale("th").add(543, "years").format("D MMMM YYYY");
    },
    todayEn() {
      return moment().locale("en").format("D MMMM YYYY");
    },
    ptFullname() {
      return [
        this.patient.titleNameTh,
        this.patient.firstNameTh,
        this.patient.lastNameTh,
      ]
        .filter(Boolean)
        .join(" ");
    },
    ptFullnameEn() {
      const en = [
        this.patient.titleNameEn,
        this.patient.firstNameEn,
        this.patient.lastNameEn,
      ]
        .filter(Boolean)
        .join(" ");
      return en || this.ptFullname;
    },
    ptFullnameDisplay() {
      return this.isTh ? this.ptFullname : this.ptFullnameEn;
    },
    doctorList() {
      return this.getDoctorList.map(function (doctor) {
        var user = this.getUserList.find(function (user) {
          return user.uid === doctor.uid;
        });
        return Object.assign({}, doctor, {
          drLicenseNo: user ? user.drLicenseNo : null,
        });
      }, this);
    },
    filteredDoctorList() {
      const q = (this.doctorSearch || "").trim().toLowerCase();
      if (!q) return this.doctorList;
      return this.doctorList.filter((d) => {
        return (
          (d.fullName || "").toLowerCase().includes(q) ||
          (d.drLicenseNo || "").toLowerCase().includes(q)
        );
      });
    },
    doctorObj() {
      return (
        this.doctorList.find((doctor) => this.selectId === doctor.uid) || {}
      );
    },
    doctorDisplayName() {
      const d = this.doctorObj;
      if (!d) return "";
      if (!this.isTh) return d.fullNameEn || d.fullName || "";
      return d.fullName || "";
    },
    titleDisplay() {
      if (!this.title) return "";
      if (!this.isTh) return "Dr.";
      return this.title;
    },
    treatmentItems() {
      return this.getTreatmentHistoryList
        .map((item) => item.items)
        .flat()
        .filter((item) => [1, 2].includes(item.typeId))
        .map((item) => {
          const user = this.getUserListIncludeDeletedBy(item.actionUid, "uid");
          return {
            ...item,
            name: this.isTh
              ? item.name || ""
              : item.nameEn || item.name || "",
            doctorName: this.isTh
              ? user.fullName || ""
              : user.fullNameEn || user.fullName || "",
            dxName: this.getTreatDiagBy(item.diagItems, "id").nameEn,
            toothNumberSurfaces: `${item.toothItems || ""} ${this.formatSurfaces(
              item
            )}`,
          };
        });
    },
    filteredTreatmentItems() {
      const q = (this.pickerSearch || "").trim().toLowerCase();
      const sorted = this.treatmentItems
        .slice()
        .sort((a, b) => Date.parse(b.creationDt) - Date.parse(a.creationDt));
      if (!q) return sorted;
      return sorted.filter((it) => {
        return (
          ((it.toothNumberSurfaces || "") + "")
            .toLowerCase()
            .includes(q) ||
          (it.dxName || "").toLowerCase().includes(q) ||
          (it.doctorName || "").toLowerCase().includes(q) ||
          (it.name || "").toLowerCase().includes(q)
        );
      });
    },
    groupSelectedItem() {
      const sorted = this.selectedItem
        .slice(0)
        .sort((a, b) => Date.parse(a.creationDt) - Date.parse(b.creationDt))
        .map((item) => ({
          ...item,
          formattedDate: this.formatDate(item.creationDt),
        }));
      return sorted.reduce((acc, item) => {
        const date = item.formattedDate;
        if (!acc[date]) acc[date] = [];
        acc[date].push(item);
        return acc;
      }, {});
    },
    documentData() {
      return {
        title: this.title,
        doctorObj: this.doctorObj,
        ptFullname: this.ptFullname,
        ptFullnameEn: this.ptFullnameEn,
        patient: this.patient,
        selectedItem: this.selectedItem,
        groupSelectedItem: this.groupSelectedItem,
        commentDetail: this.commentDetail,
        today: this.today,
        todayEn: this.todayEn,
        lang: this.lang,
        showLogo: this.showLogo,
        headerFormat: this.headerFormat,
        logoUrl: this.logo,
      };
    },
    statusMessage() {
      const missing = [];
      if (!this.title) {
        missing.push(this.isTh ? "คำนำหน้า" : "title");
      }
      if (!this.selectId) {
        missing.push(this.isTh ? "แพทย์" : "doctor");
      }
      if (this.selectedItem.length === 0) {
        missing.push(this.isTh ? "รายการรักษา" : "treatments");
      }
      if (missing.length === 0) {
        return this.isTh
          ? `พร้อมพิมพ์ — เลือก ${this.selectedItem.length} รายการ`
          : `Ready — ${this.selectedItem.length} treatment(s) selected`;
      }
      return this.isTh
        ? `กรุณาระบุ: ${missing.join(" · ")}`
        : `Please provide: ${missing.join(" · ")}`;
    },
  },
  methods: {
    show(patient) {
      this.$refs[this.modalRef].show();
      this.clinic = (this.branchInfo && this.branchInfo.clinic) || {};
      this.patient = patient || {};
      this.logo =
        (this.branchInfo &&
          this.branchInfo.clinic &&
          this.viewFile(this.branchInfo.clinic.logo)) ||
        null;
    },
    hideModal() {
      this.$refs[this.modalRef].hide();
    },
    isSelected(item) {
      return this.selectedItem.some((s) => s.id === item.id);
    },
    toggleSelected(item) {
      const i = this.selectedItem.findIndex((s) => s.id === item.id);
      if (i === -1) this.selectedItem = [...this.selectedItem, item];
      else
        this.selectedItem = this.selectedItem.filter((_, idx) => idx !== i);
    },
    onCommentInput(e) {
      const txt = (e.target.innerText || "").replace(/\u00A0/g, " ").trim();
      this.commentDetail = txt;
    },
    onCommentBlur(e) {
      this.onCommentInput(e);
      if (!this.commentDetail) this.isEditingComment = false;
    },
    startEditingComment() {
      this.isEditingComment = true;
      this.$nextTick(() => {
        const el = this.$refs.commentEditor;
        if (el) el.focus();
      });
    },
    clearAll() {
      this.selectedItem = [];
      this.commentDetail = "";
      this.title = null;
      if (!this.isDoctor) this.selectId = null;
      this.pickerSearch = "";
      this.doctorSearch = "";
      this.isEditingComment = false;
      this.$v.$reset();
    },
    formatDate(date) {
      if (!date) return "";
      if (this.isTh) {
        return moment(date)
          .add(543, "years")
          .locale("th")
          .format("D MMM YYYY");
      }
      return moment(date).locale("en").format("D MMM YYYY");
    },
    hasLicense(d) {
      const v = d && d.drLicenseNo;
      return Boolean(v) && v !== "null" && v !== "undefined";
    },
    formatSurfaces(item) {
      var surfaces = [
        { id: 5, label: "O", order: 1 },
        { id: 3, label: "M", order: 2 },
        { id: 4, label: "D", order: 3 },
        { id: 1, label: "B", order: 4 },
        { id: 2, label: "Li", order: 5 },
      ];
      if (!item.surfaceItems) return "";
      return item.surfaceItems
        .map(function (surfaceId) {
          return surfaces.find(function (s) {
            return s.id === surfaceId;
          });
        })
        .filter(Boolean)
        .sort(function (a, b) {
          return a.order - b.order;
        })
        .map(function (surface) {
          return surface.label;
        })
        .join("");
    },
    handleShowSignDialog(data) {
      this.$refs.Dialog.showAlertConfirm(
        data.title,
        `${data.message}\n\nกรุณาแตะปุ่มด้านล่างเพื่อไปยังหน้าเซ็นต์เอกสาร`,
        {
          okTitle: "เปิดหน้าเซ็นต์เอกสาร",
          cancelTitle: "ยกเลิก",
        }
      ).then((result) => {
        if (result.isConfirmed) {
          window.open(data.url, "_blank", "noopener,noreferrer");
        }
      });
    },
    async generatePdf() {
      this.$v.$touch();
      if (this.$v.$invalid) return;
      this.isPdfCreating = true;
      const bodyEl = this.$refs.body;
      try {
        await this.$nextTick();
        if (bodyEl) bodyEl.classList.add("is-printing");
        await this.$refs.pdfExport.generatePdf({
          body: bodyEl,
        });
      } finally {
        if (bodyEl) bodyEl.classList.remove("is-printing");
        this.isPdfCreating = false;
        this.$v.$reset();
      }
    },
    async uploadPdf() {
      this.$v.$touch();
      if (this.$v.$invalid) return;
      this.isPdfCreating = true;
      const bodyEl = this.$refs.body;
      const isIOS =
        /iPad|iPhone|iPod/.test(navigator.userAgent) ||
        (navigator.platform === "MacIntel" && navigator.maxTouchPoints > 1);
      if (isIOS) {
        this.$refs.pdfExport.userInteractionContext = {
          timestamp: Date.now(),
        };
      }
      try {
        await this.$nextTick();
        if (bodyEl) bodyEl.classList.add("is-printing");
        await this.$refs.pdfExport.generatePdf({
          body: bodyEl,
          option: {
            isUpload: true,
            patientId: this.patient.id,
            remarkTitle: "MedicalCert_",
          },
        });
      } finally {
        if (bodyEl) bodyEl.classList.remove("is-printing");
        this.isPdfCreating = false;
        this.$v.$reset();
      }
    },
  },
  mounted() {
    if (this.isDoctor && this.userInfo && this.userInfo.uid) {
      this.selectId = this.userInfo.uid;
    }
    const sl = localStorage.getItem("medicalCertShowLogo");
    if (sl !== null) this.showLogo = sl === "true";
    const hf = localStorage.getItem("medicalCertHeaderFormat");
    if (hf && ["clinic_branch", "clinic", "branch"].includes(hf)) {
      this.headerFormat = hf;
    }
  },
  watch: {
    showLogo(v) {
      localStorage.setItem("medicalCertShowLogo", String(v));
    },
    headerFormat(v) {
      localStorage.setItem("medicalCertHeaderFormat", v);
    },
  },
};
</script>

<style lang="scss">
.medical-cert-editor-modal {
  .modal-dialog {
    max-width: 1280px;
    margin: 1rem auto;
  }

  .modal-body {
    padding: 0;
    background: #f4f4f4;
  }

  .modal-header {
    padding: 0.75rem 1rem;
  }

  .mce-title-bar {
    display: flex;
    align-items: center;
    gap: 12px;
    flex: 1;
  }

  .mce-title-icon {
    width: 28px;
    height: 28px;
    border-radius: 6px;
    background: #eef0f3;
    color: #495057;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    font-size: 14px;
  }

  .mce-title-text {
    font-weight: 600;
    font-size: 16px;
  }

  .mce-title-sub {
    font-weight: 400;
    color: #6c757d;
    font-size: 13px;
    margin-left: 4px;
  }

  .mce-lang {
    margin-left: auto;
  }

  .mce-body {
    display: flex;
    align-items: stretch;
    min-height: 720px;
    max-height: calc(100vh - 220px);
  }

  .mce-picker {
    width: 380px;
    flex-shrink: 0;
    border-right: 1px solid #dee2e6;
    background: #fff;
    display: flex;
    flex-direction: column;
    overflow: hidden;
  }

  .mce-picker-head {
    padding: 12px 16px 10px;
    border-bottom: 1px solid #e9ecef;
    background: #fff;
    position: sticky;
    top: 0;
    z-index: 1;
  }

  .mce-picker-title {
    display: flex;
    align-items: center;
    justify-content: space-between;
    font-size: 12px;
    text-transform: uppercase;
    letter-spacing: 0.06em;
    color: #6c757d;
    margin-bottom: 8px;
  }

  .mce-picker-count {
    font-family: ui-monospace, "JetBrains Mono", monospace;
    font-size: 11px;
    background: #f1f3f5;
    color: #495057;
    padding: 2px 8px;
    border-radius: 10px;
    letter-spacing: 0;
  }

  .mce-picker-search {
    font-size: 12px;
  }

  .mce-picker-list {
    flex: 1;
    overflow-y: auto;
  }

  .mce-picker-empty {
    padding: 32px 16px;
    text-align: center;
    color: #adb5bd;
    font-size: 13px;
  }

  .mce-row {
    width: 100%;
    text-align: left;
    background: #fff;
    border: 0;
    border-bottom: 1px solid #f1f3f5;
    padding: 10px 16px;
    display: flex;
    gap: 10px;
    cursor: pointer;
    transition: background 0.1s;
  }

  .mce-row:hover {
    background: #f8f9fa;
  }

  .mce-row--selected {
    background: #f0f0f0;
    box-shadow: inset 4px 0 0 #161616;
  }

  .mce-row--selected:hover {
    background: #e9ecef;
  }

  .mce-row-check {
    flex-shrink: 0;
    color: #495057;
    padding-top: 2px;
  }

  .mce-row--selected .mce-row-check {
    color: #161616;
  }

  .mce-row-body {
    flex: 1;
    min-width: 0;
    display: flex;
    flex-direction: column;
    gap: 2px;
  }

  .mce-row-line1 {
    display: flex;
    justify-content: space-between;
    align-items: baseline;
    gap: 8px;
  }

  .mce-row-title {
    font-weight: 600;
    font-size: 13px;
    color: #161616;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    flex: 1;
    min-width: 0;
  }

  .mce-row-date {
    font-size: 11px;
    color: #868e96;
    white-space: nowrap;
    flex-shrink: 0;
  }

  .mce-row-dx {
    font-size: 12px;
    color: #495057;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  .mce-row-meta {
    font-size: 11px;
    color: #868e96;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    display: flex;
    align-items: baseline;
    gap: 0;
  }

  .mce-row-tooth {
    font-family: ui-monospace, "JetBrains Mono", monospace;
    font-weight: 500;
    color: #495057;
  }

  .mce-row-sep {
    margin: 0 5px;
    opacity: 0.6;
  }

  .mce-canvas {
    flex: 1;
    overflow-y: auto;
    padding: 32px 24px 56px;
    background: #f4f4f4;
  }

  .mce-paper {
    background: #fff;
    border: 1px solid #d1d5da;
    box-shadow: 4px 4px 0 #e3e3e3;
    max-width: 720px;
    margin: 0 auto;
    padding: 40px 50px;
    position: relative;
  }

  // Header zone toolbar — hover-revealed strip above .mc-head.
  // Bbox sits flush against the top of .mc-head (bottom: 100%) so the mouse
  // never crosses a hover gap when moving up from the header into the toolbar.
  // Visual breathing room comes from padding-bottom inside the toolbar; the
  // outer rect still touches .mc-head, keeping :hover continuous.
  .mce-headzone {
    position: absolute;
    bottom: 100%;
    left: 0;
    right: 0;
    display: flex;
    gap: 16px;
    background: #161616;
    color: #fff;
    padding: 6px 10px 12px;
    font-size: 11px;
    border-radius: 4px 4px 0 0;
    box-shadow: 0 4px 16px rgba(0, 0, 0, 0.15);
    opacity: 0;
    visibility: hidden;
    transition: opacity 0.15s ease, visibility 0s linear 0.15s;
    z-index: 5;
  }

  .mc-head:hover .mce-headzone,
  .mc-head:focus-within .mce-headzone,
  .mce-headzone:hover {
    opacity: 1;
    visibility: visible;
    transition: opacity 0.15s ease, visibility 0s linear 0s;
  }

  .mc-head:hover,
  .mc-head:focus-within {
    outline: 2px dashed rgba(22, 22, 22, 0.35);
    outline-offset: 6px;
  }

  .mce-headzone-grp {
    display: flex;
    align-items: center;
    gap: 8px;
  }

  .mce-headzone-lbl {
    color: #adb5bd;
    text-transform: uppercase;
    letter-spacing: 0.06em;
    font-size: 10px;
  }

  .mce-headzone .btn {
    font-size: 11px;
    padding: 2px 8px;
    line-height: 1.3;
  }

  // Editable pill (title / doctor)
  .mce-pill {
    display: inline !important;
    background: #fffbe0 !important;
    border: 0 !important;
    border-bottom: 2px dashed #161616 !important;
    color: #161616 !important;
    font-weight: 600 !important;
    font-size: 12.5px !important;
    padding: 0 6px 1px !important;
    border-radius: 0 !important;
    font-family: inherit !important;
    text-decoration: none !important;
    box-shadow: none !important;
    cursor: pointer;
    transition: background 0.12s;
  }

  .mce-pill:hover,
  .mce-pill:focus {
    background: #fff3a0 !important;
    text-decoration: none !important;
  }

  .mce-pill-placeholder {
    color: #6c757d;
    font-weight: 400;
    font-style: italic;
  }

  .mce-pill--invalid > .mce-pill {
    border-bottom-color: #dc3545 !important;
    background: #ffe0e0 !important;
    color: #b91c1c !important;
    box-shadow: 0 0 0 2px rgba(220, 53, 69, 0.18) !important;
    animation: mcePillInvalidPulse 1.6s ease-in-out infinite;
  }

  @keyframes mcePillInvalidPulse {
    0%,
    100% {
      box-shadow: 0 0 0 2px rgba(220, 53, 69, 0.18);
    }
    50% {
      box-shadow: 0 0 0 4px rgba(220, 53, 69, 0.32);
    }
  }

  .mce-pill-menu--wide {
    min-width: 280px;
    max-height: 320px;
    overflow-y: auto;
  }

  .mce-doctor-row {
    display: flex;
    justify-content: space-between;
    align-items: baseline;
    gap: 12px;
    width: 100%;
  }

  .mce-doctor-name {
    font-weight: 500;
  }

  .mce-doctor-lic {
    font-size: 11px;
    color: #868e96;
    font-family: ui-monospace, "JetBrains Mono", monospace;
    white-space: nowrap;
  }

  // Editable comment
  .mce-comment-edit {
    min-height: 60px;
    padding: 8px 10px;
    margin: 4px 0 10px;
    border: 1.5px dashed transparent;
    border-radius: 2px;
    background: #fffbe0;
    cursor: text;
    outline: none;
    text-align: justify;
    line-height: 1.7;
    transition: border-color 0.12s, background 0.12s;
  }

  .mce-comment-edit:hover {
    border-color: rgba(22, 22, 22, 0.25);
  }

  .mce-comment-edit:focus {
    border-color: #161616;
    background: #fff;
  }

  // Empty state — 4 dashed rule lines on a yellow background, so the
  // empty opinion area reads as "editable" the same way .mce-comment-edit
  // and the .mce-pill triggers do.
  .mce-comment-empty {
    cursor: text;
    padding: 8px 10px;
    margin: 4px 0 10px;
    border-radius: 2px;
    background: #fffbe0;
    transition: background 0.12s;
  }

  .mce-comment-empty:hover {
    background: #fff3a0;
  }

  // Footer
  .mce-footer {
    padding: 12px 16px;
    border-top: 1px solid #dee2e6;
    background: #fff;
    display: flex;
    align-items: center;
    gap: 12px;
  }

  .mce-status {
    flex: 1;
    text-align: center;
    color: #6c757d;
    font-size: 12.5px;
  }

}


// .mc-declare sets text-indent: 3em + text-align: justify, both of which
// inherit through to the dropdown menu's items (Bootstrap's .dropdown-item
// uses text-align: inherit and never resets text-indent). Without this
// reset, every menu row gets a 48px first-line indent. Padding is tightened
// from Bootstrap's default 1.5rem to keep the menu compact.
.mce-pill-menu .dropdown-item,
.mce-pill-menu .b-dropdown-form,
.mce-pill-menu .mce-doctor-search,
.mce-pill-menu .dropdown-text {
  text-indent: 0;
  text-align: left;
}

.mce-pill-menu .dropdown-item {
  padding: 6px 12px;
}

.mce-pill-menu .b-dropdown-form,
.mce-pill-menu .mce-doctor-search {
  padding: 6px 8px;
}

// Print suppression — applied globally because PdfExport clones the body
// and re-renders it outside the modal wrapper, so any rule nested under
// .medical-cert-editor-modal would no longer match.
.is-printing {
  .mce-headzone {
    display: none !important;
  }

  .mc-head {
    outline: none !important;
  }

  // The dropdown wrapper renders <button class="btn dropdown-toggle mce-pill">
  // — strip the yellow background, dashed underline becomes solid, no caret.
  .mce-pill,
  button.mce-pill,
  .btn.mce-pill,
  .dropdown-toggle.mce-pill {
    background: transparent !important;
    background-color: transparent !important;
    border: 0 !important;
    border-bottom: 0.5px solid #161616 !important;
    border-radius: 0 !important;
    padding: 0 4px 1px !important;
    color: #161616 !important;
    box-shadow: none !important;
    font-weight: 600 !important;
  }

  .mce-pill::after,
  .b-dropdown::after,
  .b-dropdown > .dropdown-toggle::after {
    display: none !important;
  }

  .mce-pill-placeholder {
    display: none !important;
  }

  // The b-dropdown wrapper element itself shouldn't introduce any visual
  // chrome at print time — it's inline by default but force inline-block
  // to ensure it sits in the prose paragraph cleanly.
  .b-dropdown {
    display: inline !important;
    background: transparent !important;
    box-shadow: none !important;
  }

  // Hide the menu entirely (Bootstrap normally hides it via .show toggle,
  // but if it's still in the DOM in the cloned tree, force it gone).
  .dropdown-menu {
    display: none !important;
  }

  .mce-comment-edit {
    background: transparent !important;
    border: none !important;
    padding: 0 !important;
    margin: 0 0 10px !important;
    outline: none !important;
  }

  .mce-comment-empty {
    background: transparent !important;
    cursor: default !important;
  }
}
</style>
